"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Minus } from "lucide-react";
import { toast } from "sonner";
import { SELECTION_UNIT_KG, KG_QUANTITY_STEP } from "@/constants/misc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface AddToCartControlsProps {
  quantity: number;
  onAdd: () => void;
  onDecrement: () => void;
  onIncrement: () => void;
  onQuantityChange?: (qty: number) => void;
  variant?: "icon" | "full" | "compact";
  unit?: string;
  maxQuantity?: number | null;
}

export default function AddToCartControls({
  quantity,
  onAdd,
  onDecrement,
  onIncrement,
  onQuantityChange,
  variant = "icon",
  unit,
  maxQuantity,
}: AddToCartControlsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  
  const step = unit === SELECTION_UNIT_KG ? KG_QUANTITY_STEP : 1;

  useEffect(() => {
    if (dialogOpen) {
      setInputValue(String(quantity));
    }
  }, [dialogOpen, quantity]);

  const atMax = maxQuantity != null && quantity >= maxQuantity;

  const notifyAtMax = () => {
    toast.info(`La cantidad máxima es de ${maxQuantity}${unitLabel ? ` ${unitLabel}` : ""}`);
  };

  const handleConfirmQuantity = () => {
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed) && parsed >= 0 && onQuantityChange) {
      const clamped = maxQuantity != null ? Math.min(parsed, maxQuantity) : parsed;
      if (maxQuantity != null && parsed > maxQuantity) notifyAtMax();
      onQuantityChange(Math.round(clamped * 10) / 10);
    }
    setDialogOpen(false);
  };

  const openDialog = () => {
    if (onQuantityChange) setDialogOpen(true);
  };

  const unitLabel = unit === "kg" ? "kg" : unit === "piece" ? "pz" : unit ? unit : "";

  const quantityDialog = onQuantityChange && (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="sm:max-w-xs" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Cantidad</DialogTitle>
        </DialogHeader>
        <Input
          type="number"
          min={0}
          step={step}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirmQuantity();
          }}
          autoFocus
          className="text-center text-lg"
        />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" className="cursor-pointer mr-2" onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button className="cursor-pointer bg-teal-400 hover:bg-teal-500" onClick={handleConfirmQuantity}>
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (variant === "compact") {
    return (
      <>
        <div
          className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-foreground cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openDialog();
          }}
        >
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 rounded-full p-0 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDecrement();
            }}
          >
            {quantity === step ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          </Button>

          <span className="min-w-[2rem] text-center text-sm">
            {quantity}{unitLabel && <span className="ml-0.5 text-xs">{unitLabel}</span>}
          </span>

          <Button
            size="icon"
            variant="ghost"
            className={`h-7 w-7 rounded-full p-0 cursor-pointer${atMax ? " opacity-30" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (atMax) { notifyAtMax(); return; }
              onIncrement();
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {quantityDialog}
      </>
    );
  }

  if (variant === "full") {
    return (
      <>
        <div className="w-full">
          {quantity === 0 ? (
            <Button
              className="w-full h-12 bg-teal-400 hover:bg-teal-500 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
            >
              <Plus className="h-5 w-5 mr-2" />
              Agregar
            </Button>
          ) : (
            <div
              className="flex items-center justify-between rounded-lg border bg-white px-4 h-13 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                openDialog();
              }}
            >
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 rounded-full cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onDecrement();
                }}
              >
                {quantity === step ? <Trash2 className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
              </Button>

              <span className="text-lg font-medium">
                {quantity}{unitLabel && <span className="ml-1 text-sm">{unitLabel}</span>}
              </span>

              <Button
                size="icon"
                variant="ghost"
                className={`h-9 w-9 rounded-full cursor-pointer${atMax ? " opacity-30" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (atMax) { notifyAtMax(); return; }
                  onIncrement();
                }}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
        {quantityDialog}
      </>
    );
  }

  return (
    <>
      <div className="absolute bottom-2 right-2 left-2 z-[5] flex">
        {quantity === 0 ? (
          <Button
            size="icon"
            className="pointer-events-auto ml-auto h-10 w-10 rounded-full bg-teal-400 hover:bg-teal-500 cursor-pointer shadow-md transition group-hover:scale-105"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAdd();
            }}
          >
            <Plus className="h-6 w-6" />
          </Button>
        ) : (
          <div
            className="flex items-center justify-between w-full md:w-auto md:max-w-full md:ml-auto rounded-full bg-white py-1 px-1 text-xs font-medium text-foreground shadow-md cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openDialog();
            }}
          >
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0 rounded-full p-0 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDecrement();
              }}
            >
              {quantity === step ? <Trash2 className="h-[18px] w-[18px]" /> : <Minus className="h-[18px] w-[18px]" />}
            </Button>

            <span className="min-w-0 md:px-3 lg:px-3 xl:px-5 text-center text-sm">
              {quantity}{unitLabel && <span className="ml-0.5 text-xs">{unitLabel}</span>}
            </span>

            <Button
              size="icon"
              variant="ghost"
              className={`h-8 w-8 shrink-0 rounded-full p-0 cursor-pointer${atMax ? " opacity-30" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (atMax) { notifyAtMax(); return; }
                onIncrement();
              }}
            >
              <Plus className="h-[18px] w-[18px]" />
            </Button>
          </div>
        )}
      </div>
      {quantityDialog}
    </>
  );
}
